import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Dumbbell, Users, Home as HomeIcon } from "lucide-react";
import { siteData, realImages } from "../mock";
import { COACHING_SPECIALTIES } from "../data/specialties";
import { coachUrl } from "../data/urls";
import Reviews from "../components/Reviews";
import FAQ from "../components/FAQ";
import CTASection from "../components/CTASection";
import RelatedArticles from "../components/RelatedArticles";
import SEO from "../components/SEO";
import { PAGE_META } from "../data/pageMeta";

const benefits = [
  { icon: HomeIcon, title: "À domicile", desc: "Séances au calme, dans votre environnement familier." },
  { icon: Dumbbell, title: "Programme sur mesure", desc: "Chaque séance est conçue selon vos objectifs santé." },
  { icon: Users, title: "Tous profils", desc: "Adapté seniors, post-rééduc, maladies chroniques." },
];

export default function Coaching() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        {...PAGE_META["/coach-domicile"]}
        keywords={["coach sportif Toulouse", "sport à domicile", "sport santé", "crédit d'impôt", "Saint-Orens", "Balma"]}
      />
      <section className="container-tight py-16 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="chip mb-6">Coaching à domicile</span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-[#1a1815] tracking-tight leading-[1.05]">
            Coach sportif à domicile à Toulouse
          </h1>
          <p className="mt-4 font-serif text-2xl text-[#4a4642] leading-snug">
            Un programme sur mesure qui vient à vous
          </p>
          <p className="mt-5 text-lg text-[#4a4642] leading-relaxed">
            Vous voulez reprendre le sport, retrouver de l'énergie ou soulager vos douleurs — mais sans vous déplacer ? J'interviens directement chez vous.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#f0e4d5] text-[#b85e42] px-4 py-2 text-sm font-medium">
            50% de réduction immédiate · Crédit d'impôt SAP
          </div>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a
              href="https://www.resalib.fr/praticien/120570-jean-christophe-happillon-fasciatherapeute-quint-fonsegrives"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
            >
              Bilan gratuit <ArrowRight size={16} />
            </a>
            <a href={`tel:${siteData.practitioner.phone.replace(/\s/g, "")}`} className="btn-outline">
              {siteData.practitioner.phone}
            </a>
          </div>
        </div>
        <div className="rounded-[2rem] overflow-hidden aspect-[4/5]">
          <img
            src={realImages.strengthMedball}
            alt="Coach sportif à domicile Toulouse"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <section className="container-tight py-16">
        <div className="grid md:grid-cols-3 gap-4">
          {benefits.map((b, i) => (
            <div key={i} className="bg-white rounded-3xl p-8 border border-[#e8e0d5]">
              <div className="w-12 h-12 rounded-2xl bg-[#f7f2ec] flex items-center justify-center mb-5">
                <b.icon size={22} className="text-[#d97757]" />
              </div>
              <h3 className="font-serif text-xl font-medium mb-2 text-[#1a1815]">{b.title}</h3>
              <p className="text-[#4a4642] text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#f5ead9] to-[#fce8d5] py-24">
        <div className="container-tight max-w-3xl">
          <span className="chip mb-4">Pour qui ?</span>
          <h2 className="font-serif text-4xl font-medium leading-tight mb-8 text-[#2b2620]">
            Un accompagnement pour votre situation
          </h2>
          <ul className="space-y-4">
            {[
              "Cours adaptés aux maladies chroniques",
              "Reprise du sport en sécurité après opération ou rééducation",
              "Suivi personnalisé après vos séances de kiné ou d'ostéo",
              "Programme unique adapté à vos objectifs de forme et santé",
              "Approche mobilité pour garder articulations et tissus en bonne santé",
              "Préparation physique vers un mouvement plus sécuritaire et économique",
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-[#5c554c]">
                <CheckCircle2 size={20} className="text-[#c76d4b] flex-shrink-0 mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Reviews />
      <FAQ />

      {/* Coaching specialty internal linking */}
      <section className="container-tight py-16">
        <div className="max-w-2xl mb-10">
          <span className="chip mb-4">Programmes adaptés</span>
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2b2620] tracking-tight leading-tight">
            Mes coachings spécialisés
          </h2>
          <p className="mt-3 text-[#5c554c]">Chaque profil bénéficie d'un accompagnement sur mesure.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {COACHING_SPECIALTIES.map((s) => (
            <Link key={s.slug} to={coachUrl(s.slug)} className="group bg-white rounded-2xl border border-[#eee5d6] p-6 hover:border-[#c76d4b]/40 hover:shadow-md transition-all">
              <h3 className="font-serif text-lg font-medium text-[#2b2620] group-hover:text-[#c76d4b] transition-colors">
                {s.keyword.charAt(0).toUpperCase() + s.keyword.slice(1)}
              </h3>
              <p className="text-sm text-[#5c554c] mt-2 line-clamp-2 leading-relaxed">{s.intro.substring(0, 130)}…</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#c76d4b]">
                Découvrir <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <RelatedArticles
        category="Sport santé"
        title="À lire pour aller plus loin"
        subtitle="Nos articles sur la reprise du sport, la mobilité et le sport santé à Toulouse et alentours."
      />
      <CTASection />
    </>
  );
}
